import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FormControl,
  FormLabel,
  Input,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Flex,
  Box,
  Image,
  Text,
  FormErrorMessage
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { UseFormRegisterReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useManifest } from '../../context/ManifestContext.tsx';
import {
  ManifestTelephoneCountryOption,
  toLocalizedTelephoneCountryOptions,
} from '../../utils/manifestCountry.ts';
import { getFlagUrl } from '../../utils/flagUrl';

interface AleaTelephoneProps {
  label: string;
  value: { code: string; phone: string };
  onChange: (value: { code: string; phone: string }) => void;
  register: UseFormRegisterReturn;
  error: string | undefined;
  isRequired?: boolean;
}

const AleaTelephone: React.FC<AleaTelephoneProps> = ({
  label,
  value,
  onChange,
  register,
  error,
  isRequired,
}) => {
  const { t } = useTranslation();
  const { countries } = useManifest();
  const countryOptions = useMemo(
    () => toLocalizedTelephoneCountryOptions(countries, t),
    [countries, t]
  );
  const defaultCountry = countryOptions.find((option) => option.value === 'DE') || countryOptions[0] || null;
  const [selectedCountry, setSelectedCountry] = useState<ManifestTelephoneCountryOption | null>(
    null
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const [isCountryMenuOpen, setIsCountryMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [menuWidth, setMenuWidth] = useState<number | null>(null);

  useEffect(() => {
    const country = countryOptions.find((option) => option.label === value.code);
    if (country) {
      setSelectedCountry(country);
    } else if (!value.code || value.code === '') {
      setSelectedCountry(defaultCountry);
    } else if (!selectedCountry) {
      setSelectedCountry(defaultCountry);
    }
  }, [value.code, countryOptions, defaultCountry, selectedCountry]);

  useEffect(() => {
    if (isCountryMenuOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [isCountryMenuOpen]);

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPhoneNumber = e.target.value;
    onChange({ code: selectedCountry?.label || value.code || '', phone: newPhoneNumber });
    register.onChange(e); // Call react-hook-form's onChange
  };

  const handleCountryChange = (option: ManifestTelephoneCountryOption) => {
    setSelectedCountry(option);
    onChange({ code: option.label, phone: value.phone });
    setIsCountryMenuOpen(false);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);
  };

  const filteredCountryOptions = countryOptions
    .filter((option) => {
      const q = searchQuery.trim().toLowerCase();
      if (!q) return true;
      return (
        option.name.toLowerCase().includes(q) ||
        option.value.toLowerCase().includes(q) ||
        option.label.toLowerCase().includes(q) ||
        String(option.code).includes(q)
      );
    })
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

  const formLabelStyles = {
    fontWeight: isRequired ? 500 : 300,
    marginBottom: '3px',
    color: 'alea.500',
    paddingLeft: '16px',
  };

  const menuButtonStyles = {
    borderRightRadius: 0,
    border: '0',
    borderColor: 'gray.300',
    minW: '63px',
    height: '50px',
    bg: 'alea.500',
    color: 'white',
    padding: 0,
    paddingLeft: '15px',
    borderLeftRadius: '20px',
    _focus: { background: 'alea.500' },
    _focusVisible: { background: 'alea.500' },
    _active: { background: 'alea.500' }
  };

  const menuListStyles = {
    height: '200px',
    overflowY: 'auto',
    width: '100%',
    minW: '100%',
    marginLeft: '0',
    paddingTop: '0',
    paddingBottom: '0',
    top: '0',
    borderRadius: '0',
    marginTop: '-1px'
  } as const;

  const selectedCountryLabelStyles = {
    color: '#C6C6C6',
    paddingLeft: '8px',
    paddingRight: '4px',
    fontSize: '18px'
  };

  const inputStyles = {
    border: 'none',
    _focus: { outline: 'none' },
    paddingLeft: '2px',
    flex: 1,
    height: '48px',
    color: '#000000',
    _focusVisible: { borderColor: 'alea.500' },
  };

  return (
    <FormControl isInvalid={!!error}>
      <FormLabel sx={formLabelStyles}>{label}</FormLabel>
      <Flex ref={containerRef}>
        <Menu
          isOpen={isCountryMenuOpen}
          onOpen={() => {
            if (countryOptions.length === 0) return;
            setIsCountryMenuOpen(true);
            if (containerRef.current) {
              setMenuWidth(containerRef.current.offsetWidth);
            }
            setSearchQuery('');
            setTimeout(() => searchInputRef.current?.focus(), 0);
          }}
          onClose={() => setIsCountryMenuOpen(false)}
          closeOnBlur={false}
          placement="bottom-start"
          gutter={0}
          autoSelect={false}
          strategy="fixed"
          modifiers={[{ name: 'flip', enabled: false }]}
        >
          <MenuButton
            as={Button}
            {...menuButtonStyles}
            isDisabled={countryOptions.length === 0}
            sx={isCountryMenuOpen ? { borderBottomLeftRadius: 0 } : undefined}
          >
            <Flex alignItems="center">
              <Box mr="0">
                {selectedCountry ? (
                  <Image
                    src={getFlagUrl(selectedCountry.value)}
                    alt={selectedCountry.value}
                    borderRadius="20px"
                    width="20px"
                    height="20px"
                  />
                ) : (
                  <Box width="20px" height="20px" />
                )}
              </Box>
              <Box as={ChevronDownIcon} color="white" boxSize="24px" />
            </Flex>
          </MenuButton>
          <MenuList sx={{
            ...menuListStyles,
            width: menuWidth ? `${menuWidth}px` : menuListStyles.width,
            minW: menuWidth ? `${menuWidth}px` : menuListStyles.minW,
            marginLeft: '0'
          }}>
            {filteredCountryOptions.length === 0 && (
              <Flex alignItems="center" justifyContent="center" height="100%" color="#666" fontSize="14px">
                {t('general.noResults')}
              </Flex>
            )}
            {filteredCountryOptions.map((option) => (
              <MenuItem
                key={option.value}
                onClick={() => handleCountryChange(option)}
                px="0"
                sx={{ height: '50px', borderBottom: '1px solid #DCDCDC' }}
              >
                <Flex alignItems="center">
                  <Box ml="15px" mr="10px">
                    <Image
                      src={getFlagUrl(option.value)}
                      alt={option.value}
                      boxSize="20px"
                      width="20px"
                      height="20px"
                      borderRadius="20px"
                    />
                  </Box>
                  <Text color="#000" fontSize="16px" fontWeight={400}>
                    {option.name} ({option.label})
                  </Text>
                </Flex>
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
        <Flex
          flex="1"
          alignItems="center"
          border="1px solid"
          borderColor={isCountryMenuOpen ? 'white' : 'gray.300'}
          borderLeft={isCountryMenuOpen ? 'none' : 'none'}
          borderRightRadius="20px"
          bg="white"
          position="relative"
          height="50px"
          top="0px"
        >
          {isCountryMenuOpen && (
            <Input
              ref={searchInputRef}
              placeholder={t('search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
              position="absolute"
              top={"-1px"}
              left={0}
              width="100%"
              height="50px"
              bg="white"
              border="1px solid"
              borderColor={isCountryMenuOpen ? 'gray.300' : 'white'}
              borderRadius="20px"
              borderTopLeftRadius="0"
              borderBottomLeftRadius="0"
              borderBottomRightRadius="0"
              zIndex={1}
              focusBorderColor="none"
              _focus={{ boxShadow: 'none', borderColor: 'gray.300' }}
              px="7px"
            />
          )}
          <Text sx={selectedCountryLabelStyles}>{selectedCountry?.label || value.code || ''}</Text>
          <Input
            ref={inputRef}
            value={value.phone}
            onChange={handlePhoneNumberChange}
            name={register.name}
            onBlur={register.onBlur}
            sx={inputStyles}
            bg="white"
            borderRadius="20px"
            fontSize="18px"
          />
        </Flex>
      </Flex>
      {error && <FormErrorMessage>{error}</FormErrorMessage>}
    </FormControl>
  );
};

export default AleaTelephone;